import { FC, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v4'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import { useGetClientsQuery, useCreateClientMutation } from '../redux/hooks.ts'
import type { ProjectCreate, ProjectUpdate } from '@itemize/server/models'

// Type for client option with support for creating new clients
interface ClientOptionType {
  inputValue?: string
  id?: string
  name: string
}

// Form validation schema using Zod
const projectFormSchema = z.object({
  name: z.string().min(1, 'Project name is required'),
  clientId: z.string().optional(),
  description: z.string().optional(),
})

export type ProjectFormData = z.infer<typeof projectFormSchema>

const filter = createFilterOptions<ClientOptionType>()

export interface ProjectFormModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: ProjectCreate | ProjectUpdate) => Promise<void>
  initialData?: Partial<ProjectFormData>
  title: string
  submitText: string
  isLoading?: boolean
}

export const ProjectFormModal: FC<ProjectFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData = {},
  title,
  submitText,
  isLoading = false,
}) => {
  const { data: clients = [], isLoading: clientsLoading } = useGetClientsQuery()
  const [createClient] = useCreateClientMutation()
  const [value, setValue] = useState<ClientOptionType | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: '',
      clientId: '',
      description: '',
    },
    mode: 'onChange',
  })

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      const formValues = {
        name: initialData?.name || '',
        clientId: initialData?.clientId || '',
        description: initialData?.description || '',
      }
      reset(formValues)
      clearErrors()

      // Set the value based on initial clientId
      if (initialData?.clientId) {
        const client = clients.find((c) => c.id === initialData.clientId)
        setValue(client ? { id: client.id, name: client.name } : null)
      } else {
        setValue(null)
      }
    }
  }, [
    open,
    initialData?.name,
    initialData?.clientId,
    initialData?.description,
    reset,
    clearErrors,
    clients,
  ])

  const handleFormSubmit = async (data: ProjectFormData) => {
    try {
      let clientId = data.clientId

      // If value has inputValue, it means a new client was selected
      if (value && value.inputValue) {
        // Create a new value from the user input
        const newClient = await createClient({
          name: value.inputValue.trim(),
          phone: undefined,
        }).unwrap()
        clientId = newClient.id
      } else if (!clientId) {
        // If no client is selected, show error
        setError('clientId', {
          type: 'manual',
          message: 'Please select a client or create a new one',
        })
        return
      }

      // Ensure clientId is set
      if (!clientId) {
        setError('clientId', {
          type: 'manual',
          message: 'Please select a client',
        })
        return
      }

      // Transform data to match backend expectations
      const transformedData: ProjectCreate | ProjectUpdate = {
        name: data.name.trim(),
        clientId,
        ...(data.description &&
          data.description.trim() && { description: data.description.trim() }),
      }

      await onSubmit(transformedData)
      onClose()
    } catch (error) {
      // Set a general form error
      setError('root', {
        type: 'manual',
        message: error instanceof Error ? error.message : 'An error occurred',
      })
    }
  }

  const handleClose = () => {
    reset()
    clearErrors()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {errors.root && <Alert severity="error">{errors.root.message}</Alert>}

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Project Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  required
                  autoFocus
                />
              )}
            />

            <Controller
              name="clientId"
              control={control}
              render={({ field: { onChange, value: formValue, ...field } }) => {
                const clientOptions: ClientOptionType[] = clients.map((client) => ({
                  id: client.id,
                  name: client.name,
                }))

                return (
                  <Autocomplete
                    {...field}
                    value={value}
                    onChange={(_: unknown, newValue: ClientOptionType | string | null) => {
                      if (typeof newValue === 'string') {
                        // User typed a string directly
                        setValue({
                          inputValue: newValue,
                          name: newValue,
                        })
                        onChange('')
                      } else if (newValue && newValue.inputValue) {
                        // Create a new value from the user input
                        setValue({
                          inputValue: newValue.inputValue,
                          name: newValue.inputValue,
                        })
                        onChange('')
                      } else {
                        // User selected an existing client
                        setValue(newValue)
                        onChange(newValue?.id || '')
                      }
                    }}
                    filterOptions={(options: (string | ClientOptionType)[], params) => {
                      const filtered = filter(options as ClientOptionType[], params)
                      const { inputValue } = params

                      // Suggest the creation of a new value
                      const isExisting = (options as ClientOptionType[]).some(
                        (option: ClientOptionType) => inputValue === option.name,
                      )
                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          name: `Add "${inputValue}"`,
                        })
                      }

                      return filtered
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    options={clientOptions}
                    getOptionLabel={(option: ClientOptionType | string) => {
                      // Value selected with enter, right from the input
                      if (typeof option === 'string') {
                        return option
                      }
                      // Add "xxx" option created dynamically
                      if (option.inputValue) {
                        return option.inputValue
                      }
                      // Regular option
                      return option.name
                    }}
                    loading={clientsLoading}
                    disabled={clientsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client"
                        required
                        error={!!errors.clientId}
                        helperText={errors.clientId?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {clientsLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option: string | ClientOptionType) => {
                      const { key, ...optionProps } = props
                      const optionName = typeof option === 'string' ? option : option.name
                      return (
                        <Box component="li" key={key} {...optionProps}>
                          {optionName}
                        </Box>
                      )
                    }}
                    noOptionsText={clientsLoading ? 'Loading clients...' : 'No clients found'}
                    freeSolo
                  />
                )
              }}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading || isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || isSubmitting || clientsLoading}
          >
            {isSubmitting ? 'Saving...' : submitText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
